/**
 * PlayerListUI - Always-visible player list with mini health bars
 */
export class PlayerListUI {
    private container: HTMLDivElement;
    private playerItems: Map<string, HTMLDivElement> = new Map();

    constructor() {
        this.container = document.createElement('div');
        this.container.id = 'player-list-ui';
        this.container.style.cssText = `
            position: fixed;
            top: 10px;
            left: 10px;
            display: flex;
            flex-direction: column;
            gap: 8px;
            z-index: 100;
            pointer-events: auto;
        `;
        document.body.appendChild(this.container);
    }

    addPlayer(id: string, name: string, color: number): void {
        const item = document.createElement('div');
        item.style.cssText = `
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 4px 8px;
            background: rgba(0,0,0,0.6);
            border-radius: 20px;
            cursor: default;
            transition: all 0.2s;
            border: 1px solid transparent;
        `;

        // Color circle
        const circle = document.createElement('div');
        circle.style.cssText = `
            width: 14px;
            height: 14px;
            border-radius: 50%;
            background: #${color.toString(16).padStart(6, '0')};
            border: 1px solid rgba(255,255,255,0.3);
        `;

        // Name
        const nameEl = document.createElement('span');
        nameEl.textContent = name;
        nameEl.style.cssText = `
            color: #fff;
            font-size: 12px;
            font-family: Arial, sans-serif;
            font-weight: bold;
            min-width: 40px;
        `;

        // Health bar (mini)
        const healthBarContainer = document.createElement('div');
        healthBarContainer.style.cssText = `
            width: 50px;
            height: 6px;
            background: #333;
            border-radius: 3px;
            overflow: hidden;
        `;

        const healthBarFill = document.createElement('div');
        healthBarFill.className = 'health-bar-fill';
        healthBarFill.style.cssText = `
            width: 100%;
            height: 100%;
            background: #00b894;
            transition: width 0.3s, background 0.3s;
        `;

        healthBarContainer.appendChild(healthBarFill);
        item.appendChild(circle);
        item.appendChild(nameEl);
        item.appendChild(healthBarContainer);
        this.container.appendChild(item);
        this.playerItems.set(id, item);
    }

    updateHealth(id: string, percent: number): void {
        const item = this.playerItems.get(id);
        if (!item) return;
        const bar = item.querySelector('.health-bar-fill') as HTMLDivElement;
        if (bar) {
            const clampedPercent = Math.max(0, Math.min(100, percent));
            bar.style.width = `${clampedPercent}%`;

            let color = '#00b894';
            if (clampedPercent <= 25) color = '#d63031';
            else if (clampedPercent <= 50) color = '#fdcb6e';
            bar.style.background = color;
        }
    }

    setClickable(clickable: boolean, onSelect: (id: string) => void): void {
        this.playerItems.forEach((item, id) => {
            if (clickable) {
                item.style.cursor = 'pointer';
                item.style.border = '1px solid rgba(255,255,255,0.3)';
                item.onclick = (e) => {
                    e.stopPropagation();
                    // Remove highlight from all
                    this.playerItems.forEach(i => i.style.background = 'rgba(0,0,0,0.6)');
                    // Highlight selected
                    item.style.background = 'rgba(108, 92, 231, 0.8)';
                    onSelect(id);
                };
            } else {
                item.style.cursor = 'default';
                item.style.border = '1px solid transparent';
                item.onclick = null;
            }
        });
    }

    markDead(id: string): void {
        const item = this.playerItems.get(id);
        if (item) {
            item.style.opacity = '0.4';
            const bar = item.querySelector('.health-bar-fill') as HTMLDivElement;
            if (bar) bar.style.width = '0%';
        }
    }

    destroy(): void {
        this.container.remove();
    }
}
